import { useLocation, useMatches } from '@remix-run/react';
import {
  browserProfilingIntegration,
  browserTracingIntegration,
  consoleLoggingIntegration,
  init as sentryInit,
  replayIntegration,
  type ErrorEvent,
} from '@sentry/remix';
import { useEffect } from 'react';

const IGNORED_USER_AGENTS = [/headlesschrome/i, /electron/i];

// For some reason the ErrorEvent does not include title, which is present in the JSON
// payload sent to Sentry and available in the Sentry UI, so we are including it.
type OverloadedErrorEvent = ErrorEvent & { title?: string };

export function init() {
  sentryInit({
    dsn: ENV.SENTRY_DSN,
    enableLogs: ENV.SENTRY_ENV?.toLowerCase() !== 'pe',
    environment: ENV.SENTRY_ENV,
    beforeSend(event: OverloadedErrorEvent) {
      if (event.request?.url) {
        const url = new URL(event.request.url);

        if (url.protocol === 'chrome-extension:' || url.protocol === 'moz-extension:') {
          // This error is from a browser extension, ignore it
          return null;
        }
      }

      if (ENV.SENTRY_ENV?.toLowerCase() !== 'pe') {
        console.log('Sentry error event (beforeSend):', event);
      }

      if (
        (event.title && event.title.toLowerCase().includes('hydration error')) ||
        event.exception?.values?.some(
          (ex) =>
            ex.type?.toLowerCase() === 'error' &&
            ex.value &&
            ex.value.toLowerCase().includes('minified react error #418'),
        )
      ) {
        // Tag the event for easy grouping
        event.tags = {
          ...event.tags,
          error_type: 'hydration_error',
        };
      } else if (
        event.exception?.values?.some(
          (ex) =>
            ex.type?.toLowerCase() === 'referenceerror' &&
            ex.value &&
            (ex.value.toLowerCase().includes('variable: al_') ||
              ex.value.toLowerCase().includes('variable: chartboost')),
        )
      ) {
        // ReferenceError: Can't find variable: al_onAppWillResignActive, etc.
        // ReferenceError: Can't find variable: Chartboost
        event.tags = {
          ...event.tags,
          error_type: 'known_reference_error',
        };
      }

      return event;
    },
    beforeSendTransaction(event) {
      if (IGNORED_USER_AGENTS.some((ua) => ua.test(navigator.userAgent))) {
        return null;
      }
      return event;
    },
    integrations: [
      browserTracingIntegration({
        useEffect,
        useLocation,
        useMatches,
      }),
      replayIntegration({
        // NOTE: Returning false will skip upload of the replay
        beforeErrorSampling(event: OverloadedErrorEvent) {
          if (ENV.SENTRY_ENV?.toLowerCase() !== 'pe') {
            console.log('Sentry error event (beforeErrorSampling):', event);
          }

          if (
            (event.title && event.title.toLowerCase().includes('hydration error')) ||
            event.exception?.values?.some(
              (ex) =>
                ex.type?.toLowerCase() === 'error' &&
                ex.value &&
                ex.value.toLowerCase().includes('minified react error #418'),
            )
          ) {
            return false;
          } else if (
            event.exception?.values?.some(
              (ex) =>
                ex.type?.toLowerCase() === 'referenceerror' &&
                ex.value &&
                (ex.value.toLowerCase().includes('variable: al_') ||
                  ex.value.toLowerCase().includes('variable: chartboost')),
            )
          ) {
            // ReferenceError: Can't find variable: al_onAppWillResignActive
            // ReferenceError: Can't find variable: Chartboost
            return false;
          }

          return true;
        },
      }),
      browserProfilingIntegration(),
      consoleLoggingIntegration({ levels: ['log'] }),
    ],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 0.1,

    // Capture Replay for 0% of all sessions,
    // plus for 50% of sessions with an error
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.5,
  });
}
